/**
 * The only thing allowed to open an incoming row.
 *
 * Opening is spending: the ratchet destroys a message key the moment it is
 * used, so a row can be opened exactly once and every later attempt fails.
 * Three call sites used to do it whenever they felt like it — the socket
 * delivering a message, the history page, the chat-list preview drawing a
 * snippet — and whichever asked second reported a perfectly good message as
 * broken, with nobody touching the phone.
 *
 * So everything comes through here, and here it is:
 *
 *   1. answered from the archive when the row has already been opened;
 *   2. serialised per session, because two messages of one conversation
 *      opened at once race for the same ratchet state;
 *   3. written down BEFORE the ratchet is allowed to move.
 *
 * Point 3 is the one that replaces a transaction we cannot have: the archive
 * is IndexedDB and the session lives in the Keychain, and nothing spans both.
 * Order gives the same guarantee. Move the ratchet first and a crash in
 * between destroys the message permanently — the key is gone and the text was
 * never stored. Write the text first and the same crash costs one repeated
 * decryption, which succeeds, because the session never moved.
 *
 * Specification: docs/behavior/chat-message-lifecycle.md
 */

import { conversationId } from './messageStore.mjs';
import { rowFromPlaintext, unb64 } from './chatCrypto.mjs';

/** Three failures in a row from one device, and we stop waiting for it to fix itself. */
export const SESSION_LOST_AFTER = 3;
/** No more than one rebuild request per device per five minutes. */
export const RESET_COOLDOWN_MS = 5 * 60 * 1000;

/** Which of these is worth acting on, and which is just noise. */
export const INTAKE_ERROR = {
  DUPLICATE: 'E_DUPLICATE',              // already opened once — not an error
  NO_SESSION: 'E_NO_SESSION',            // needs a handshake from the other side
  DIVERGED: 'E_CHAINS_DIVERGED',         // session exists, keys do not agree
  STALE_PREKEY: 'E_STALE_PREKEY',        // handshake names a key we dropped
  ENVELOPE: 'E_ENVELOPE',                // shape we do not understand
  NO_SENDER: 'E_NO_SENDER',              // row without a sending device
  LEGACY_AFTER_CUTOFF: 'E_LEGACY_AFTER_CUTOFF', // plaintext row from after this dialog went E2EE
};

/**
 * The legacy cutoff: after the first message of a dialog has been opened,
 * a row without `enc_v` is not history any more.
 *
 * Plaintext rows have no author anyone can check — the server that lost
 * the right to read still has the right to write, straight into SQLite,
 * past the socket handler that refuses plaintext (§7г). Before this dialog
 * ever had a session such rows ARE the history, and there is nothing to
 * compare them against. From the first opened frame on, a plaintext row
 * newer than it can only be an insertion; it is buried as unsupported and
 * its text is never shown (ADR-011 §2.1 п.8, call it §7е).
 *
 * Returns the error code to bury the row under, or null for genuine history.
 */
export function legacyVerdict(cutoff, row) {
  if (!cutoff || !row || row.enc_v === 1) return null;
  if (row.id == null || !Number.isFinite(Number(row.id))) return INTAKE_ERROR.LEGACY_AFTER_CUTOFF;
  return Number(row.id) > Number(cutoff.id) ? INTAKE_ERROR.LEGACY_AFTER_CUTOFF : null;
}

/**
 * Tell "already opened" apart from "cannot be opened".
 *
 * The ratchet says only "no" to both, and they are opposite problems: one
 * means the message is fine and somebody asked twice, the other means the
 * session has to be rebuilt. The header carries the answer — a message number
 * below the one we are expecting is one we have already consumed.
 */
function classify(reason, diag) {
  if (!reason) return INTAKE_ERROR.ENVELOPE;
  if (diag && diag.why === 'mac' && typeof diag.n === 'number' && typeof diag.ourNr === 'number'
      && diag.n < diag.ourNr) {
    return INTAKE_ERROR.DUPLICATE;
  }
  if (reason.includes('already opened')) return INTAKE_ERROR.DUPLICATE;
  if (reason.includes('no session')) return INTAKE_ERROR.NO_SESSION;
  if (reason.includes('signed prekey') || reason.includes('one-time prekey')) return INTAKE_ERROR.STALE_PREKEY;
  if (reason.includes('chains do not agree')) return INTAKE_ERROR.DIVERGED;
  if (reason.includes('own copy')) return INTAKE_ERROR.DUPLICATE;
  return INTAKE_ERROR.ENVELOPE;
}

export class ChatIntake {
  /**
   * `archive` is a MessageStore. `sessions` is a ChatSessions. `onOpened` is
   * called with every row that just became readable, so the screen can react
   * without polling.
   */
  constructor({ sessions, archive, myUserId, onOpened = null, onSessionLost = null, onResendNeeded = null }) {
    this.sessions = sessions;
    this.archive = archive;
    this.myUserId = Number(myUserId);
    this.onOpened = onOpened;
    /**
     * Called when a device's session is judged lost, so the layer above can
     * rebuild it. Signal does the same thing in AutomaticSessionResetJob:
     * archive the session, tell the person in the conversation, and send an
     * empty message that carries a fresh handshake — rate limited, because a
     * reset storm between two confused devices is worse than silence.
     */
    this.onSessionLost = onSessionLost;
    /**
     * Called for a message this device could not open, so the sender can be
     * asked to send that one again.
     *
     * Rebuilding the session fixes the future; this is the only thing that
     * recovers the message that was already lost. Signal calls it a retry
     * receipt — the failing side names the message, and the sender resends it
     * from its own copy, which it still holds in the clear.
     */
    this.onResendNeeded = onResendNeeded;
    this._asked = new Map();
    /** One promise chain per (peer, device): the lock, in a single-threaded world. */
    this._chains = new Map();
    /** Consecutive failures per device, and when we last asked for a rebuild. */
    this._failures = new Map();
    this._lastReset = new Map();
    /** Per-conversation legacy cutoff, read once from the archive's meta. */
    this._cutoffs = new Map();
  }

  /** {id, at} of the first opened frame in this conversation, or null. */
  async legacyCutoff(conv) {
    if (this._cutoffs.has(conv)) return this._cutoffs.get(conv);
    let cut = null;
    try { cut = (await this.archive.getMeta(conv)).legacyCutoff || null; } catch (e) { cut = null; }
    this._cutoffs.set(conv, cut);
    return cut;
  }

  /**
   * The first frame that opens in a dialog draws the line. Set once and
   * never moved: moving it forward would re-admit plaintext, and there is
   * no honest event that could justify that.
   */
  async _noteOpened(conv, opened) {
    if (opened.id == null || await this.legacyCutoff(conv)) return;
    const cut = { id: Number(opened.id), at: this._now() };
    this._cutoffs.set(conv, cut);
    try { await this.archive.setMeta(conv, { legacyCutoff: cut }); } catch (e) { /* best effort; re-derived next open */ }
  }

  /** A plaintext row: history before the cutoff, an insertion after it. */
  async _legacy(row) {
    const conv = this._conv(row);
    const code = legacyVerdict(await this.legacyCutoff(conv), row);
    if (!code) return this._store(row);
    // Buried WITHOUT its text: what the server wrote is exactly what must
    // not be shown. A tombstone, so the next sync walks past it (see
    // _archived) instead of warning about the same row for ever.
    const tomb = { ...row, content: '', msgType: 'text', decryptError: code };
    return this._store(tomb);
  }

  /**
   * Decide whether this failure means the session is gone.
   *
   * One failure proves nothing — a message can arrive out of order, or be a
   * repeat. Three in a row from the same device, none of them duplicates,
   * means our half of the ratchet no longer matches theirs, and the far side
   * cannot know: it believes the session is up and will keep sending. So the
   * first move is ours.
   */
  _noteFailure(row, code) {
    const key = `${row.from_user_id}:${row.sender_device_id}`;
    if (code === INTAKE_ERROR.DUPLICATE) { this._failures.delete(key); return; }
    if (code !== INTAKE_ERROR.NO_SESSION && code !== INTAKE_ERROR.DIVERGED) return;

    const count = (this._failures.get(key) || 0) + 1;
    this._failures.set(key, count);
    if (count < SESSION_LOST_AFTER) return;

    const last = this._lastReset.get(key) || 0;
    const now = this._now();
    if (now - last < RESET_COOLDOWN_MS) {
      console.warn('[CHAT] session with', key, 'still failing, but a rebuild was asked for',
        Math.round((now - last) / 1000), 's ago — waiting');
      return;
    }
    this._lastReset.set(key, now);
    this._failures.set(key, 0);
    console.warn('[CHAT] session lost with user', row.from_user_id,
      'device', String(row.sender_device_id).slice(0, 8), `— ${count} failures in a row, asking for a rebuild`);
    if (this.onSessionLost) {
      try {
        this.onSessionLost({ peerUserId: row.from_user_id, deviceId: row.sender_device_id });
      } catch (e) { console.warn('[CHAT] rebuild request failed:', e?.message); }
    }
  }

  /**
   * Ask the sender for this one message, once.
   *
   * Once per message, not once per attempt: the same row is looked at every
   * time a chat opens, and a request per look would be a request per glance at
   * the screen.
   */
  _askForResend(row, code) {
    if (code !== INTAKE_ERROR.NO_SESSION && code !== INTAKE_ERROR.DIVERGED) return;
    if (!this.onResendNeeded || !row.client_mid) return;
    const key = `${row.from_user_id}:${row.client_mid}`;
    const last = this._asked.get(key) || 0;
    const now = this._now();
    if (now - last < RESET_COOLDOWN_MS) return;
    this._asked.set(key, now);
    console.warn('[CHAT] asking user', row.from_user_id, 'to send message',
      String(row.client_mid).slice(0, 8), 'again — this device could not open it');
    try {
      this.onResendNeeded({
        peerUserId: row.from_user_id,
        clientMid: row.client_mid,
        deviceId: this.sessions && this.sessions.deviceId,
      });
    } catch (e) { console.warn('[CHAT] resend request failed:', e?.message); }
  }

  /** Overridable so tests do not have to wait five minutes. */
  _now() { return Date.now(); }

  _conv(row) {
    const other = Number(row.from_user_id) === this.myUserId ? row.to_user_id : row.from_user_id;
    return conversationId(this.myUserId, other);
  }

  /** Serialise everything touching one session's state. */
  _queue(key, work) {
    const prev = this._chains.get(key) || Promise.resolve();
    const next = prev.then(work, work);
    this._chains.set(key, next.catch(() => {}));
    return next;
  }

  async _archived(row) {
    if (row.id == null) return null;
    const held = await this.archive.getMessages(this._conv(row));
    const hit = held.find(m => m && m.id === row.id);
    if (!hit) return null;
    // An archived FAILURE usually means "try again" — a resend may have
    // redeemed the session since. Except a tombstone: a v1 handshake or a
    // spent own-copy key will never open, and exhuming it on every sync is
    // what kept the storm-era rows warning for ever.
    if (hit.decryptError && hit.decryptError !== INTAKE_ERROR.ENVELOPE
        && hit.decryptError !== INTAKE_ERROR.DUPLICATE
        && hit.decryptError !== INTAKE_ERROR.LEGACY_AFTER_CUTOFF) return null;
    return hit;
  }

  /**
   * Open one row, or hand back the copy already opened.
   *
   * Rows that are not ours to open (a copy addressed to another device) come
   * back marked `skip`, so the caller drops them instead of showing an error
   * for somebody else's mail.
   */
  async accept(row) {
    if (!row) return row;
    if (row.enc_v !== 1) return this._legacy(row);             // plaintext era — or an insertion (§7е)

    const mine = this.sessions && this.sessions.deviceId;
    if (row.target_device_id && mine && row.target_device_id !== mine) {
      return { ...row, skip: true };
    }
    if (!row.sender_device_id) return { ...row, decryptError: INTAKE_ERROR.NO_SENDER };

    const held = await this._archived(row);
    if (held) return { ...held, read: row.read != null ? row.read : held.read };

    const key = `${row.from_user_id}:${row.sender_device_id}`;
    return this._queue(key, () => this._open(row));
  }

  /** Same, for a page of history. Order is preserved. */
  async acceptAll(rows) {
    if (!Array.isArray(rows)) return [];
    const out = [];
    for (const r of rows) out.push(await this.accept(r));
    return out;
  }

  async _open(row) {
    // Somebody ahead of us in the queue may have opened it already.
    const held = await this._archived(row);
    if (held) return { ...held, read: row.read != null ? row.read : held.read };

    const { out, commit, reason, diag } = await this.sessions.openMessage(
      row.from_user_id, row.sender_device_id, unb64(row.content),
    );

    if (!out) {
      const code = classify(reason, diag);
      // A duplicate is a normal outcome and says nothing about the session.
      // Everything else is worth a line, once, with the numbers that decide
      // what to do about it.
      if (code !== INTAKE_ERROR.DUPLICATE) {
        console.warn('[CHAT] row', row.id, 'from user', row.from_user_id,
          'device', String(row.sender_device_id).slice(0, 8), '—', reason,
          diag ? JSON.stringify(diag) : '');
      }
      this._noteFailure(row, code);
      this._askForResend(row, code);
      const failed = { ...row, decryptError: code };
      // A tombstone for a FINAL refusal. A v1 handshake or a spent own-copy
      // key will never open, and without a record of that every sync walked
      // the same corpses again — the storm-era rows warned on every start,
      // for ever. Archived, the idempotency rule (§4 п.3) skips them; the
      // merge guard (И4) still lets a real open displace the tombstone if
      // one ever arrives. Transient codes stay unarchived on purpose: a
      // resend can still redeem them, and its arrival under the same
      // client_mid is what ends their retries.
      if (code === INTAKE_ERROR.ENVELOPE || code === INTAKE_ERROR.DUPLICATE) {
        try { await this.archive.putMessages(this._conv(row), [failed]); } catch (e) { /* retried next sync */ }
      }
      return failed;
    }

    let opened;
    try {
      opened = rowFromPlaintext(row, out);
    } catch (e) {
      // The envelope did not parse. The ratchet still must not move: nothing
      // was written down, so a later version of this app can try again.
      console.warn('[CHAT] row', row.id, 'opened but its envelope did not parse:', e?.message);
      return { ...row, decryptError: INTAKE_ERROR.ENVELOPE };
    }

    // It opened, so whatever was wrong with this session is over.
    this._failures.delete(`${row.from_user_id}:${row.sender_device_id}`);

    // A handshake carrier. It exists to rebuild the session and has done that
    // by opening; it is not a message and must not reach the archive or the
    // screen. The ratchet still moves — that IS the rebuild.
    if (opened.msgType === 'rekey') {
      await commit();
      return { ...opened, skip: true, rekey: true };
    }

    // Written down first, ratchet moved second. See the note at the top.
    await this.archive.putMessages(this._conv(row), [opened]);
    await commit();
    await this._noteOpened(this._conv(row), opened);
    if (this.onOpened) { try { this.onOpened(opened); } catch (e) { /* screen's problem */ } }
    return opened;
  }

  /** Plaintext-era rows still belong in the archive; the screen reads only it. */
  async _store(row) {
    if (row.id != null) {
      try { await this.archive.putMessages(this._conv(row), [row]); } catch (e) { /* cache is best effort here */ }
    }
    return row;
  }
}
